import { useState, useEffect, useCallback, useImperativeHandle, useRef } from 'react';
import { useAuth } from '../../../contexts/AuthContext';

/**
 * Module-level dedup caches (keyed by clientId).
 * Persists across renders for the same component instance.
 */
const eventCaches = {};

/**
 * Cross-render load guard (prevents double-fetch in React StrictMode and on
 * same-instance clientId reuse). Keyed by clientId.
 */
window.streamEventsLoaded = window.streamEventsLoaded || {};

/**
 * Shared hook for bond and user event streams.
 *
 * @param {object} opts
 * @param {string}   opts.clientId             - Bond ID or User ID
 * @param {Function} opts.fetchEvents           - (token, clientId, options) => Promise
 * @param {Function} opts.getWebSocketUrl       - (clientId, token) => string
 * @param {string}   opts.defaultSource         - 'mqtt' | 'user'
 * @param {Function} [opts.preprocessEvents]    - Raw API events → normalised events (bond only)
 * @param {string}   opts.visibilityKey         - Key passed to onToggleVisibility
 * @param {Function} opts.setEvents
 * @param {Function} opts.setEventsByRequestId
 * @param {Function} opts.onToggleVisibility
 * @param {object}   opts.ref                  - forwarded ref from forwardRef
 */
export function useStreamEvents({
    clientId,
    fetchEvents,
    getWebSocketUrl,
    defaultSource,
    preprocessEvents,
    visibilityKey,
    setEvents,
    setEventsByRequestId,
    onToggleVisibility,
    onRefresh,
    onHomeChange,
    ref,
}) {
    const { getToken } = useAuth();

    // Stable ref for onRefresh so onEventsBatch doesn't need it as a dependency
    const onRefreshRef = useRef(onRefresh);
    useEffect(() => { onRefreshRef.current = onRefresh; }, [onRefresh]);
    const refreshPendingRef = useRef(false);

    // Same, for the homes reload HOME events trigger
    const onHomeChangeRef = useRef(onHomeChange);
    useEffect(() => { onHomeChangeRef.current = onHomeChange; }, [onHomeChange]);
    const homeChangePendingRef = useRef(false);

    if (!eventCaches[clientId]) {
        eventCaches[clientId] = new Set();
    }

    const [isConnected, setIsConnected] = useState(false);
    const [isConnecting, setIsConnecting] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [eventSocket, setEventSocket] = useState(null);
    const [showEvents, setShowEvents] = useState(true);

    // Cleanup dedup cache on unmount
    useEffect(() => {
        return () => { delete eventCaches[clientId]; };
    }, [clientId]);

    // Clear connecting state once connected
    useEffect(() => { setIsConnecting(false); }, [isConnected]);

    const handleToggleEvents = () => {
        const next = !showEvents;
        setShowEvents(next);
        if (onToggleVisibility) onToggleVisibility(visibilityKey, next);
    };

    const getStartDate = () => {
        const d = new Date();
        d.setDate(d.getDate() - 14);
        return d.toISOString();
    };

    /**
     * Dedup + normalise a single raw event. Returns null if duplicate.
     */
    const processEvent = useCallback((event) => {
        if (!event) return null;

        if (!eventCaches[clientId]) eventCaches[clientId] = new Set();

        let duplicateKey;
        if (defaultSource === 'user') {
            duplicateKey = `user_${event.user_id || ''}_${event.timestamp || ''}_${event.path || ''}_${event.action || ''}`;
        } else {
            const eventAction = event.eventType || event.action;
            if (event.__src === 'cloudwatch' || event.__src === 's3') {
                duplicateKey = `${event.trace_id || event.sessionIdentifier || ''}_${eventAction || ''}_${event.timestamp || ''}`;
            } else {
                duplicateKey = `${event.i || ''}_${event.topic || ''}_${event.timestamp || ''}`;
            }
        }

        if (eventCaches[clientId].has(duplicateKey)) return null;
        eventCaches[clientId].add(duplicateKey);

        const eventSource = event.source || defaultSource;
        const eventAction = event.eventType || event.action;

        const e = {
            id: duplicateKey,
            timestamp: event.timestamp || new Date().toISOString(),
            source: eventSource,
            action: eventAction || 'message',
            payload: event,
        };

        // Bond pipeline: default to 'mqtt' when source is absent and not palantir
        if (defaultSource !== 'user' && e.source !== 'palantir' && !event.source) {
            e.source = 'mqtt';
        }

        if (defaultSource === 'user') {
            e.bond_id = event.bond_id || event.event?.bond_id || event.attributes?.bond_id || null;
        } else {
            e.bond_id = event.bond_id;
        }

        e.sourceAction = e.source + ' ' + e.action;
        e.payloadStr = JSON.stringify(event);

        return e;
    }, [clientId, defaultSource]);

    /**
     * Batch-process events, dedup, and merge into shared state.
     */
    const onEventsBatch = useCallback((events) => {
        if (!events || !Array.isArray(events) || events.length === 0) return;

        const processed = [];
        const byRequestId = {};

        for (const event of events) {
            if (!event) continue;
            const e = processEvent(event);
            if (!e) continue;
            processed.push(e);
            const requestId = (e.payload?.i || e.payload?.event?.i)?.toLowerCase();
            if (requestId) {
                if (!byRequestId[requestId]) byRequestId[requestId] = [];
                byRequestId[requestId].push(e);
            }
        }

        if (processed.length === 0) return;

        setEvents(prev => {
            const reqIdMap = new Map();
            prev.forEach((evt, idx) => {
                const reqId = (evt?.payload?.i || evt?.payload?.event?.i)?.toLowerCase();
                if (reqId) reqIdMap.set(reqId, idx);
            });

            const updated = [...prev];
            const toAdd = [];

            for (const e of processed) {
                if (!e || !e.timestamp) continue;
                const reqId = (e.payload?.i || e.payload?.event?.i)?.toLowerCase();
                if (reqId) {
                    const existingIdx = reqIdMap.get(reqId);
                    if (existingIdx !== undefined) {
                        const existing = updated[existingIdx];
                        const hasStatus = e.payload?.s || e.payload?.event?.s;
                        if (existing && (hasStatus || e.timestamp > existing.timestamp)) {
                            updated[existingIdx] = e;
                        }
                    } else {
                        toAdd.push(e);
                        reqIdMap.set(reqId, updated.length + toAdd.length - 1);
                    }
                } else {
                    toAdd.push(e);
                }
            }

            return toAdd.length > 0 ? [...toAdd, ...updated] : updated;
        });

        if (Object.keys(byRequestId).length > 0) {
            setEventsByRequestId(prev => {
                const next = { ...prev };
                for (const [key, vals] of Object.entries(byRequestId)) {
                    next[key] = [...(next[key] || []), ...vals];
                }
                return next;
            });
        }

        // Trigger data refresh when device or ownership events arrive in user stream
        if (defaultSource === 'user' && onRefreshRef.current && !refreshPendingRef.current) {
            const needsRefresh = processed.some(e => {
                const action = e.payload?.action;
                const type = e.payload?.type;
                return (type === 'BOND' && (action === 'DEVICE_ADD' || action === 'DEVICE_DELETE')) ||
                       type === 'OWNERSHIP' ||
                       (typeof action === 'string' && action.includes('OWNERSHIP'));
            });
            if (needsRefresh) {
                refreshPendingRef.current = true;
                setTimeout(() => {
                    onRefreshRef.current?.();
                    refreshPendingRef.current = false;
                }, 1000);
            }
        }

        // A HOME event means the homes themselves moved: renames, new homes,
        // bonds added, membership changes. Reload them so the names and bond
        // lists the feed and cards render from stop being stale.
        if (defaultSource === 'user' && onHomeChangeRef.current && !homeChangePendingRef.current) {
            if (processed.some(e => e.payload?.type === 'HOME')) {
                homeChangePendingRef.current = true;
                setTimeout(() => {
                    onHomeChangeRef.current?.();
                    homeChangePendingRef.current = false;
                }, 1000);
            }
        }
    }, [setEvents, setEventsByRequestId, processEvent, defaultSource]);

    // Load initial events on mount (guarded against StrictMode double-invoke)
    useEffect(() => {
        if (window.streamEventsLoaded[clientId]) return;
        window.streamEventsLoaded[clientId] = true;
        setIsLoading(true);

        const loadInitialEvents = async () => {
            try {
                const token = getToken();
                const data = await fetchEvents(token, clientId, {
                    start: getStartDate(),
                    end: new Date().toISOString(),
                    limit: 20,
                });
                const events = data?.type === 'logs' ? data.data : (Array.isArray(data) ? data : []);
                const toProcess = preprocessEvents ? preprocessEvents(events) : events;
                if (toProcess.length > 0) onEventsBatch(toProcess);
            } catch (error) {
                console.error('Failed to load initial events:', error);
            } finally {
                setIsLoading(false);
            }
        };

        loadInitialEvents();
    }, [clientId, onEventsBatch, getToken, fetchEvents]);

    const loadEventsWithDateRange = async (startDate, endDate, limit = 100) => {
        if (eventCaches[clientId]) eventCaches[clientId].clear();
        setIsLoading(true);
        try {
            const token = getToken();
            const data = await fetchEvents(token, clientId, {
                start: startDate ? startDate.toISOString() : getStartDate(),
                end: endDate ? endDate.toISOString() : new Date().toISOString(),
                limit,
            });
            const events = data?.type === 'logs' ? data.data : (Array.isArray(data) ? data : []);
            const toProcess = preprocessEvents ? preprocessEvents(events) : events;
            if (toProcess.length > 0) onEventsBatch(toProcess);
        } catch (error) {
            console.error('Failed to load events with date range:', error);
        } finally {
            setIsLoading(false);
        }
    };

    function connect() {
        if (isConnecting) return;
        setIsConnecting(true);
        const token = getToken();
        const url = getWebSocketUrl(clientId, token);
        const socket = new WebSocket(url);
        setEventSocket(socket);

        socket.onopen = () => setIsConnected(true);

        socket.onmessage = (event) => {
            try {
                const message = JSON.parse(event.data);
                if (message.events) {
                    const parsed = message.events
                        .map(e => { try { return JSON.parse(e); } catch { return null; } })
                        .filter(Boolean);
                    if (parsed.length > 0) onEventsBatch(parsed);
                } else {
                    onEventsBatch([message]);
                }
            } catch {
                console.error('Failed to parse WebSocket message:', event);
            }
        };

        socket.onclose = () => setIsConnected(false);
    }

    function disconnect() {
        if (eventSocket) {
            eventSocket.close();
            setEventSocket(null);
            setIsConnected(false);
        }
    }

    useImperativeHandle(ref, () => ({
        connect: () => { if (!isConnected && !isConnecting) { setIsConnecting(true); connect(); } },
        disconnect: () => { if (isConnected) disconnect(); },
        toggleVisibility: handleToggleEvents,
        isConnected: () => isConnected,
        isVisible: () => showEvents,
        loadEventsWithDateRange,
    }));

    return { isConnected, isConnecting, isLoading, showEvents, handleToggleEvents, connect, disconnect };
}
